import { useCallback, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, View, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, Alert } from "react-native";
import { useGlobalSearchParams, router, usePathname, useFocusEffect, type Href } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/hooks/useAuth";
import { ShiftType } from "@/types/db/shifts";
import { supabase } from "@/lib/supabase-client";
import { timecardService } from "@/services/timecardService";
import { expenseService } from "@/services/expenseService";
import { ExpenseType } from "@/types/db/expenses";
import { incidentService } from "@/services/incidentService";
import { IncidentType } from "@/types/db/incidents";
import { useResolvedId } from "@/hooks/useResolvedId";
import { MaterialCommunityIcons, FontAwesome5 } from "@expo/vector-icons";
import { Comments } from "@/components/Comments";
import {
  ModernCard,
  HeroSection,
  StatusBadge,
  ActionButton,
  PositionCard,
  AttachmentsCard,
  MetaTab,
} from "@/components/crew";
import useToast from "@/hooks/useToast";
import Modal from "@/components/Modal";
import { QRCodeModal } from "@/components/Modals/QRCodeModal";
import { ClockOutSurvey, ClockOutSurveyData } from "@/components/ClockOutSurvey";
import { SupervisorContactModal } from "@/components/crew/SupervisorContactModal";

interface TimeCardState {
  id?: string;
  clockedIn?: Date;
  lunchStart?: Date;
  lunchEnd?: Date;
  clockedOut?: Date;
}

export default function ShiftDetailsPage() {
  // useGlobalSearchParams (not Local): nested [shift] routes do not hand Local the parent param -> stuck loading
  const params = useGlobalSearchParams<{ id?: string; shift?: string }>();
  const shiftId = useResolvedId("home", (params.id as string) ?? (params.shift as string));
  const pathname = usePathname();
  const { colors } = useTheme();
  const { user, displayNames } = useAuth();
  const me = user?.id ?? "";
  const toast = useToast();

  const getParentPath = () => {
    const segments = pathname.split("/");
    // Dynamic parent route — expo-router can't statically verify a computed path.
    return segments.slice(0, -1).join("/") as Href;
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | false>(false);
  const [err, setErr] = useState<string | null>(null);
  const [shift, setShift] = useState<ShiftType | null>(null);
  const [cardWidth, setCardWidth] = useState(0);
  const [timeCard, setTimeCard] = useState<TimeCardState>({});
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [showClockOutSurvey, setShowClockOutSurvey] = useState(false);
  const [showSupervisorContact, setShowSupervisorContact] = useState(false);
  const [expenses, setExpenses] = useState<ExpenseType[]>([]);
  const [expensesLoading, setExpensesLoading] = useState(true);
  const [incidents, setIncidents] = useState<IncidentType[]>([]);
  const [incidentsLoading, setIncidentsLoading] = useState(true);

  // Fetch shift
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!shiftId) return;
      setLoading(true);
      setErr(null);
      try {
        const { data: fnData, error: fnError } = await supabase.functions.invoke("get-shift", {
          body: { id: shiftId },
        });
        if (fnError) throw new Error(fnError.message || "Failed to fetch shift");
        const row = (fnData as ShiftType) ?? null;
        if (!alive) return;
        setShift(row);
        if (!row) setErr(`${displayNames?.shifts?.name || "Shift"} not found.`);

        // Fetch existing timecard if user has accepted the shift
        try {
          const existingTimeCard = await timecardService.getByShiftAndUser(shiftId, me);
          if (existingTimeCard && existingTimeCard.time) {
            setTimeCard({
              id: existingTimeCard.id,
              clockedIn: existingTimeCard.time.start_time ? new Date(existingTimeCard.time.start_time) : undefined,
              lunchStart: existingTimeCard.time.meal_start ? new Date(existingTimeCard.time.meal_start) : undefined,
              lunchEnd: existingTimeCard.time.meal_end ? new Date(existingTimeCard.time.meal_end) : undefined,
              clockedOut: existingTimeCard.time.end_time ? new Date(existingTimeCard.time.end_time) : undefined,
            });
          }
        } catch (timecardError) {
          console.warn("Failed to fetch existing timecard:", timecardError);
          // Don't set error state for this, just continue without existing timecard
        }
      } catch (e: any) {
        if (!alive) return;
        setErr(e?.message ?? `Failed to load ${displayNames?.shifts?.name?.toLowerCase() || "shift"}.`);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [shiftId, me, displayNames.shifts.name]);

  useFocusEffect(() => {
    console.log("index");
  });
  // Mark shift as opened in meta_data when visiting detail page
  useEffect(() => {
    if (shiftId && me) {
      supabase.functions.invoke("respond-shift", {
        body: { shiftId, action: "update_meta", meta: { opened: true } },
      });
    }
  }, [shiftId, me]);

  // Fetch expenses for this shift by current user
  useEffect(() => {
    if (!shiftId || !me) return;
    let alive = true;
    (async () => {
      setExpensesLoading(true);
      try {
        const data = await expenseService.getExpensesByShiftAndUser(shiftId, me);
        if (alive) setExpenses(data);
      } catch (e) {
        console.error("Failed to load expenses:", e);
      } finally {
        if (alive) setExpensesLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [shiftId, me]);

  // Fetch incidents for this shift by current user
  useEffect(() => {
    if (!shiftId || !me) return;
    let alive = true;
    (async () => {
      setIncidentsLoading(true);
      try {
        const data = await incidentService.getIncidentsByShiftAndUser(shiftId, me);
        if (alive) setIncidents(data);
      } catch (e) {
        console.error("Failed to load incidents:", e);
      } finally {
        if (alive) setIncidentsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [shiftId, me]);

  // Derived UI data for current user
  const myUI = useMemo(() => {
    const positions = shift?.shift_positions ?? [];
    for (const position of positions) {
      const shiftUser = position?.shift_users?.find((su) => su.id === me);
      if (shiftUser) {
        return { ...shiftUser, position }; // { id, status, position }
      }
    }
    return null;
  }, [shift, me]);

  // Get all positions the user is invited to (for cases where user is invited to multiple positions)
  const myPositions = useMemo(() => {
    const positions = shift?.shift_positions ?? [];
    return positions.filter((position) => position.shift_users?.some((su) => su.id === me));
  }, [shift, me]);

  // Get single position for accepted shifts
  const myPosition = useMemo(() => {
    const positions = shift?.shift_positions ?? [];
    return positions.find((position) => position.shift_users?.some((su) => su.id === me));
  }, [shift, me]);

  // Filter notes that apply to the crew member's position (from both shift and project)
  // Note: position_ids in notes refer to lists.positions IDs, not shift_position IDs
  const myNotes = useMemo(() => {
    const shiftNotes = (shift?.meta_data as any)?.notes || [];
    const projectNotes = (shift?.project as any)?.meta_data?.notes || [];
    const allNotes = [...shiftNotes, ...projectNotes];
    if (!myPosition?.position_id || allNotes.length === 0) return [];
    return allNotes.filter(
      (note: any) =>
        !note.position_ids || // No position filter = visible to all
        note.position_ids.length === 0 || // Empty array = visible to all
        note.position_ids.includes(myPosition.position_id),
    );
  }, [shift?.meta_data, shift?.project, myPosition?.position_id]);

  // Get clock-in options (shift-level or inherited from project)
  const clockInOptions = useMemo(() => {
    const shiftOptions = (shift?.meta_data as any)?.clock_in_options;
    const projectOptions = (shift?.project as any)?.meta_data?.clock_in_options;
    // Use shift options if set, otherwise fall back to project options
    return shiftOptions || projectOptions || {};
  }, [shift]);

  const hasAppClockIn = clockInOptions.app === true;
  const hasQrCodeClockIn = clockInOptions.qr_code === true;
  const hasTimesheetClockIn = clockInOptions.timesheet === true;
  const hasAnyClockInMethod = hasAppClockIn || hasQrCodeClockIn || hasTimesheetClockIn;

  // Updates status for user in shift_positions[*].shift_users
  // NOTE: This is optimistic UI update only - backend handles actual DB update
  const optimisticSetStatus = useCallback(
    (status: "accepted" | "declined") => {
      const STATUS = status.toUpperCase() as "ACCEPTED" | "DECLINED";
      setShift((prev) =>
        prev
          ? {
              ...prev,
              shift_positions: (prev.shift_positions ?? []).map((p) => ({
                ...p,
                shift_users: (p?.shift_users ?? []).map((su) => (su.id === me ? { ...su, status: STATUS } : su)),
              })),
            }
          : prev,
      );
    },
    [me],
  );

  const handleAcceptDecline = useCallback(
    async (accept: boolean) => {
      if (!shift?.id) return;
      const newStatus = accept ? "accepted" : "declined";
      const before = shift;
      optimisticSetStatus(newStatus);
      setSaving(accept ? "ACCEPT" : "DECLINE");
      try {
        const { error: fnErr } = await supabase.functions.invoke("respond-shift", {
          body: { shiftId: shift.id, action: accept ? "accept" : "decline" },
        });
        if (fnErr) throw fnErr;
        toast.showToast({
          title: accept
            ? `${displayNames?.shifts?.name || "Shift"} Accepted`
            : `${displayNames?.shifts?.name || "Shift"} Declined`,
          message: accept
            ? `You have accepted this ${displayNames?.shifts?.name?.toLowerCase() || "shift"}`
            : `You have declined this ${displayNames?.shifts?.name?.toLowerCase() || "shift"}`,
          toastType: "success",
        });
      } catch (error) {
        // revert on failure
        setShift(before);
        console.error(error);
        toast.showToast({
          title: "Error",
          message: `Failed to ${accept ? "ACCEPT" : "DECLINE"} ${displayNames?.shifts?.name?.toLowerCase() || "shift"}`,
          toastType: "error",
        });
      } finally {
        setSaving(false);
        router.push(getParentPath());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shift, optimisticSetStatus],
  );

  const handleCommentsUpdate = useCallback(
    (updatedComments: any[]) => {
      // Update shift with new comments if needed
      if (shift) {
        setShift({ ...shift, comments: updatedComments });
      }
    },
    [shift],
  );

  // Handle opening kiosk mode for QR clock-in
  const handleOpenKiosk = useCallback(() => {
    setQrModalVisible(false);
    router.push(`/kiosk/${shiftId}` as any);
  }, [shiftId]);

  // Calculate shift status and times
  const shiftInfo = useMemo(() => {
    if (!shift?.start_time) return null;
    const now = new Date();
    const start = new Date(shift.start_time);
    const end = shift.end_time ? new Date(shift.end_time) : null;

    const duration = end ? Math.round(((end.getTime() - start.getTime()) / (1000 * 60 * 60)) * 10) / 10 : 0;
    const hoursWorked = timeCard.clockedIn
      ? Math.round(((now.getTime() - timeCard.clockedIn.getTime()) / (1000 * 60 * 60)) * 10) / 10
      : 0;

    let status: "upcoming" | "ready" | "active" | "completed" = "upcoming";
    if (end && now > end) status = "completed";
    else if (now > start) status = "active";
    else if (now > new Date(start.getTime() - 15 * 60 * 1000)) status = "ready";

    return {
      start,
      end,
      duration,
      hoursWorked,
      status,
      date: start.toLocaleDateString([], {
        weekday: "long",
        month: "short",
        day: "numeric",
      }),
      startTime: start.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      endTime: end?.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  }, [shift?.start_time, shift?.end_time, timeCard]);

  // Calculate total earnings across all positions (for INVITED status)
  const totalEarnings = useMemo(() => {
    if (!myPositions.length || !shiftInfo) return null;

    const totalRate = myPositions.reduce((sum, pos) => sum + (pos.payroll_rate?.rate || 0), 0);
    const totalGifted = myPositions.reduce((sum, pos) => sum + (pos.gifted_hours || 0), 0);

    return {
      rate: totalRate,
      gifted: totalGifted,
      minimum: totalGifted * totalRate,
      maximum: shiftInfo.duration * totalRate,
    };
  }, [myPositions, shiftInfo]);

  // Calculate earnings for accepted shift (single position with actual worked hours)
  const earnings = useMemo(() => {
    if (!myPosition || !shiftInfo) return null;
    const rate = myPosition.payroll_rate?.rate || 0;
    const gifted = myPosition.gifted_hours || 0;
    const worked = shiftInfo.hoursWorked;

    return {
      rate,
      gifted,
      worked,
      minimum: gifted * rate,
      current: worked * rate,
      maximum: shiftInfo.duration * rate,
    };
  }, [myPosition, shiftInfo]);

  const getExpenseStatusVariant = (status?: string): "pending" | "accepted" | "declined" | "completed" => {
    switch (status) {
      case "APPROVED":
      case "PAID":
      case "REIMBURSED":
        return "completed";
      case "REJECTED":
        return "declined";
      default:
        return "pending";
    }
  };

  const getIncidentStatusVariant = (status?: string): "pending" | "accepted" | "declined" | "completed" => {
    switch (status) {
      case "RESOLVED":
        return "completed";
      case "ESCALATED":
        return "declined";
      case "UNDER REVIEW":
        return "accepted";
      default:
        return "pending";
    }
  };

  // Time tracking handlers
  const handleClockIn = async () => {
    setSaving("clock-in");
    try {
      if (!myPosition) {
        throw new Error(`No position found for this ${displayNames?.shifts?.name?.toLowerCase() || "shift"}`);
      }

      const now = new Date();
      const timecardData = {
        shift_id: shiftId,
        user_id: me,
        shift_position_id: myPosition.id,
        shift_user_id: me, // shift_users.id is the user_id
        time: { start_time: now.toISOString() },
        project_id: shift?.project_id,
        client_id: shift?.client_id,
      };

      const newTimecard = await timecardService.addTimecard(timecardData);
      setTimeCard({ ...timeCard, id: newTimecard.id, clockedIn: now });
      toast.showToast({
        title: "Clocked In",
        message: `Started at ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
        toastType: "success",
      });
    } catch (error: any) {
      console.error("Failed to clock in:", error);
      toast.showToast({
        title: "Error",
        message: `Failed to clock in: ${error.message || "Unknown error"}`,
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Extracted clock-out logic for reuse
  const performClockOut = async () => {
    setSaving("clock-out");
    try {
      const now = new Date();
      if (!timeCard.id) throw new Error("No timecard ID found");

      // Fetch current timecard to merge time values
      const currentTimeCard = await timecardService.getTimecardById(timeCard.id);
      await timecardService.updateTimecard(timeCard.id, {
        time: { ...currentTimeCard.time, end_time: now.toISOString() },
      });
      setTimeCard({ ...timeCard, clockedOut: now });
      toast.showToast({
        title: "Clocked Out",
        message: `Ended at ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
        toastType: "success",
      });

      setTimeout(() => router.push(getParentPath()), 1500);
    } catch (error: any) {
      console.error("Failed to clock out:", error);
      toast.showToast({
        title: "Error",
        message: `Failed to clock out: ${error.message || "Unknown error"}`,
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle survey completion - check for issues requiring supervisor contact
  const handleSurveyComplete = (data: ClockOutSurveyData) => {
    // Check if meal or rest breaks were not received - requires supervisor contact
    if (data.receivedMealBreaks === false || data.receivedRestBreaks === false) {
      setShowClockOutSurvey(false);
      setShowSupervisorContact(true);
      return;
    }

    // No issues - proceed with clock out
    setShowClockOutSurvey(false);
    performClockOut();
  };

  const handleClockOut = () => {
    if (myPosition?.supervisor) {
      // Supervisor: use existing alert confirmation flow
      Alert.alert("Clock Out", "Are you sure you want to clock out?", [
        { text: "Cancel", style: "cancel" },
        {
          text: "Clock Out",
          style: "destructive",
          onPress: performClockOut,
        },
      ]);
    } else {
      // Crew member: show survey modal first
      setShowClockOutSurvey(true);
    }
  };

  const handleLunchStart = async () => {
    setSaving("lunch-start");
    try {
      const now = new Date();
      if (!timeCard.id) throw new Error("No timecard ID found");

      // Fetch current timecard to merge time values
      const currentTimeCard = await timecardService.getTimecardById(timeCard.id);
      await timecardService.updateTimecard(timeCard.id, {
        time: { ...currentTimeCard.time, meal_start: now.toISOString() },
      });
      setTimeCard({ ...timeCard, lunchStart: now });
      toast.showToast({
        title: "Lunch Started",
        message: `Break started at ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
        toastType: "success",
      });
    } catch (error: any) {
      console.error("Failed to start lunch:", error);
      toast.showToast({
        title: "Error",
        message: `Failed to start lunch: ${error.message || "Unknown error"}`,
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLunchEnd = async () => {
    setSaving("lunch-end");
    try {
      const now = new Date();
      if (!timeCard.id) throw new Error("No timecard ID found");

      // Fetch current timecard to merge time values
      const currentTimeCard = await timecardService.getTimecardById(timeCard.id);
      await timecardService.updateTimecard(timeCard.id, {
        time: { ...currentTimeCard.time, meal_end: now.toISOString() },
      });
      setTimeCard({ ...timeCard, lunchEnd: now });
      toast.showToast({
        title: "Lunch Ended",
        message: `Break ended at ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
        toastType: "success",
      });
    } catch (error: any) {
      console.error("Failed to end lunch:", error);
      toast.showToast({
        title: "Error",
        message: `Failed to end lunch: ${error.message || "Unknown error"}`,
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!shiftId || loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (err || !shift) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <MaterialCommunityIcons name="alert-circle-outline" size={48} color={colors.red.text} />
        <ThemedText style={{ marginTop: 12, fontSize: 16 }}>
          {err ?? `${displayNames?.shifts?.name || "Shift"} not found`}
        </ThemedText>
        <Button
          text="Go Back"
          icon="arrow-left"
          onPress={() => router.push(getParentPath())}
          style={{ marginTop: 16 }}
        />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === "ios" ? "padding" : "height"}>
      <ScrollView
        style={[styles.container, { backgroundColor: colors.background }]}
        contentContainerStyle={{ paddingBottom: 24 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero Section */}
        <HeroSection style={styles.hero}>
          <View style={styles.heroContent}>
            <ThemedText style={styles.heroProject} numberOfLines={2}>
              {shift.name || `Untitled ${displayNames?.shifts?.name || "Shift"}`}
            </ThemedText>
            {shift.project?.name && (
              <ThemedText style={styles.heroProjectSubtitle} numberOfLines={1}>
                {shift.project.name}
              </ThemedText>
            )}

            <View style={styles.heroDetailsRow}>
              <View style={styles.heroTimeRow}>
                <FontAwesome5 name="calendar" size={14} color="white" style={{ opacity: 0.9 }} />
                <ThemedText style={styles.heroTime}>{shiftInfo?.date}</ThemedText>
              </View>

              <View style={styles.heroTimeRow}>
                <FontAwesome5 name="clock" size={14} color="white" style={{ opacity: 0.9 }} />
                <ThemedText style={styles.heroTime}>{shiftInfo?.startTime}</ThemedText>
              </View>

              <View style={styles.heroTimeRow}>
                <MaterialCommunityIcons name="timelapse" size={14} color="white" style={{ opacity: 0.9 }} />
                <ThemedText style={styles.heroTime}>~{shiftInfo?.duration}h</ThemedText>
              </View>
            </View>

            {/* Status Badge */}
            <View style={styles.heroStatus}>
              <StatusBadge
                variant={
                  shiftInfo?.status === "active" ? "active" : shiftInfo?.status === "completed" ? "completed" : "ready"
                }
                text={shiftInfo?.status?.toUpperCase() || "UNKNOWN"}
                icon={
                  shiftInfo?.status === "active"
                    ? "clock-fast"
                    : shiftInfo?.status === "completed"
                      ? "check-circle"
                      : "clock-outline"
                }
              />
              {/* QR Code Button - Supervisor Only, when QR clock-in enabled */}
              {myPosition?.supervisor && hasQrCodeClockIn && (
                <Button
                  icon="qrcode"
                  text="Show QR"
                  onPress={() => setQrModalVisible(true)}
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderColor: "rgba(255,255,255,0.3)",
                    borderWidth: 1,
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    height: 32,
                    marginLeft: 8,
                  }}
                  textStyle={{ color: "white", fontSize: 12 }}
                />
              )}
            </View>
          </View>
        </HeroSection>

        {/* Conditional content based on status */}
        {myUI?.status === "ACCEPTED" ? (
          <>
            {/* Single Position for ACCEPTED shifts */}
            {myPosition && (
              <ModernCard style={styles.positionsCard} onLayout={(e) => setCardWidth(e.nativeEvent.layout.width)}>
                <PositionCard
                  position={{
                    name: myPosition.position?.name || "Position",
                    description: myPosition.position?.description,
                    gifted_hours: myPosition.gifted_hours,
                    payroll_rate: myPosition.payroll_rate,
                    invited_users: myPosition.shift_users?.map((su) => ({
                      id: su.id,
                      status: su.status as "ACCEPTED" | "DECLINED" | "PENDING",
                    })),
                  }}
                  userId={me}
                  totalHours={shiftInfo?.duration || 0}
                  shiftDressCode={shift?.dress_code}
                  projectDressCode={shift?.project?.dress_code}
                  style={{ backgroundColor: "transparent", borderWidth: 0, margin: 0, padding: 0 }}
                />

                {/* Position Notes */}
                <ThemedText>Notes</ThemedText>
                {myNotes.length > 0 && (
                  <View style={styles.notesSection}>
                    {myNotes.map((note: any) => (
                      <View key={note.id} style={[styles.noteItem, { backgroundColor: colors.backgroundSecondary }]}>
                        <ThemedText style={styles.noteMessage}>{note.message}</ThemedText>
                      </View>
                    ))}
                  </View>
                )}

                <MetaTab
                  width={cardWidth}
                  location={myPosition.location || shift?.location}
                  projectLocation={shift?.project?.location}
                  attachments={myPosition.attachments}
                  shiftUsers={myPosition.shift_users}
                  shiftSupervisors={shift?.shift_supervisors}
                  isSupervisor={myPosition.supervisor === true}
                  shiftDescription={shift?.meta_data?.description}
                  projectDescription={shift?.project?.meta_data?.description}
                  projectName={shift?.project?.name}
                  dressCode={shift?.dress_code}
                  abilities={shift?.shift_abilities?.map((sa) => sa.ability)}
                  restrictions={shift?.shift_restrictions?.map((sr) => sr.restriction)}
                  onsiteContact={shift?.onsite_contact_details}
                  canDecline={shiftInfo?.status !== "completed" && !timeCard.clockedIn}
                  onDecline={() => handleAcceptDecline(false)}
                  declining={saving === "DECLINE"}
                  shiftId={shiftId}
                />
              </ModernCard>
            )}

            {/* Attachments for ACCEPTED shifts */}
            {myPosition?.attachments && myPosition.attachments.length > 0 && (
              <AttachmentsCard attachments={myPosition.attachments} style={styles.attachmentsCard} />
            )}

            {/* Time Tracking Card */}
            {(timeCard.clockedIn || shiftInfo?.status === "ready" || shiftInfo?.status === "active") &&
              shiftInfo?.status !== "completed" &&
              hasAnyClockInMethod && (
                <ModernCard style={styles.timeCard}>
                  <View style={styles.cardHeader}>
                    <MaterialCommunityIcons name="clock-time-four" size={20} color={colors.text} />
                    <ThemedText type="defaultSemiBold">Time Tracking</ThemedText>
                  </View>

                  {timeCard.clockedIn && (
                    <View style={styles.timeStats}>
                      <View style={styles.timeStat}>
                        <ThemedText style={[styles.timeStatLabel, { color: colors.textSecondary }]}>
                          Clocked In
                        </ThemedText>
                        <ThemedText style={styles.timeStatValue}>
                          {timeCard.clockedIn.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </ThemedText>
                      </View>

                      <View style={styles.timeStat}>
                        <ThemedText style={[styles.timeStatLabel, { color: colors.textSecondary }]}>
                          Hours Worked
                        </ThemedText>
                        <ThemedText style={[styles.timeStatValue, { color: colors.emerald?.text || "#10b981" }]}>
                          {shiftInfo?.hoursWorked}h
                        </ThemedText>
                      </View>
                    </View>
                  )}

                  <View style={styles.actionGrid}>
                    {/* Clock In Button - Only show if app clock-in is enabled */}
                    {(shiftInfo?.status === "ready" || shiftInfo?.status === "active") &&
                      !timeCard.clockedIn &&
                      hasAppClockIn && (
                        <ActionButton
                          title={saving === "clock-in" ? "Clocking In..." : "Clock In"}
                          onPress={handleClockIn}
                          variant="success"
                          size="large"
                          icon="clock-in"
                          loading={saving === "clock-in"}
                          disabled={saving !== false}
                          fullWidth
                        />
                      )}

                    {/* QR Code Instructions - Only show if QR code clock-in is enabled and not yet clocked in */}
                    {(shiftInfo?.status === "ready" || shiftInfo?.status === "active") &&
                      !timeCard.clockedIn &&
                      hasQrCodeClockIn && (
                        <View
                          style={[
                            styles.clockInInstructions,
                            { backgroundColor: colors.cyan?.background || colors.backgroundSecondary },
                          ]}
                        >
                          <MaterialCommunityIcons
                            name="qrcode-scan"
                            size={20}
                            color={colors.cyan?.text || colors.primary}
                          />
                          <ThemedText
                            style={[styles.clockInInstructionsText, { color: colors.cyan?.text || colors.text }]}
                          >
                            Scan QR code at site to clock in
                          </ThemedText>
                        </View>
                      )}

                    {/* Supervisor Report Instructions - Only show if timesheet clock-in is enabled and not yet clocked in */}
                    {(shiftInfo?.status === "ready" || shiftInfo?.status === "active") &&
                      !timeCard.clockedIn &&
                      hasTimesheetClockIn && (
                        <View
                          style={[
                            styles.clockInInstructions,
                            { backgroundColor: colors.amber?.background || colors.backgroundSecondary },
                          ]}
                        >
                          <MaterialCommunityIcons
                            name="clipboard-text-clock"
                            size={20}
                            color={colors.amber?.text || colors.primary}
                          />
                          <ThemedText
                            style={[styles.clockInInstructionsText, { color: colors.amber?.text || colors.text }]}
                          >
                            Supervisor will record your time at end of shift
                          </ThemedText>
                        </View>
                      )}

                    <View style={styles.secondaryActions}>
                      {timeCard.clockedIn && !timeCard.lunchStart && !timeCard.clockedOut && (
                        <ActionButton
                          title={saving === "lunch-start" ? "Starting..." : "Take Lunch"}
                          onPress={handleLunchStart}
                          variant="warning"
                          icon="food"
                          loading={saving === "lunch-start"}
                          disabled={saving !== false}
                          fullWidth
                        />
                      )}

                      {timeCard.lunchStart && !timeCard.lunchEnd && (
                        <ActionButton
                          title={saving === "lunch-end" ? "Ending..." : "End Lunch"}
                          onPress={handleLunchEnd}
                          variant="success"
                          icon="food-off"
                          loading={saving === "lunch-end"}
                          disabled={saving !== false}
                          fullWidth
                        />
                      )}

                      {timeCard.clockedIn && !timeCard.clockedOut && (
                        <ActionButton
                          title={saving === "clock-out" ? "Clocking Out..." : "Clock Out"}
                          onPress={handleClockOut}
                          variant="danger"
                          icon="clock-out"
                          loading={saving === "clock-out"}
                          disabled={saving !== false}
                          fullWidth
                        />
                      )}
                    </View>
                  </View>
                </ModernCard>
              )}

            {/* Earnings Card for ACCEPTED shifts */}
            {earnings && earnings.rate > 0 && (
              <ModernCard style={styles.earningsCard}>
                <View style={styles.cardHeader}>
                  <MaterialCommunityIcons name="currency-usd" size={20} color={colors.emerald?.text || "#10b981"} />
                  <ThemedText type="defaultSemiBold">Earnings</ThemedText>
                </View>

                <View style={styles.earningsGrid}>
                  <View style={styles.earningsStat}>
                    <ThemedText style={[styles.earningsLabel, { color: colors.textSecondary }]}>Rate</ThemedText>
                    <ThemedText style={styles.earningsValue}>${earnings.rate}/hr</ThemedText>
                  </View>

                  {timeCard.clockedIn ? (
                    <View style={styles.earningsStat}>
                      <ThemedText style={[styles.earningsLabel, { color: colors.textSecondary }]}>Current</ThemedText>
                      <ThemedText style={[styles.earningsValue, { color: colors.emerald?.text || "#10b981" }]}>
                        ${earnings.current.toFixed(2)}
                      </ThemedText>
                    </View>
                  ) : (
                    <View style={styles.earningsStat}>
                      <ThemedText style={[styles.earningsLabel, { color: colors.textSecondary }]}>Estimated</ThemedText>
                      <ThemedText style={styles.earningsValue}>
                        ${earnings.minimum.toFixed(0)} - ${earnings.maximum.toFixed(0)}
                      </ThemedText>
                    </View>
                  )}
                </View>

                <View style={[styles.earningsBar, { backgroundColor: colors.backgroundSecondary }]}>
                  <View
                    style={[
                      styles.earningsProgress,
                      {
                        backgroundColor: colors.emerald?.border || "#10b981",
                        width: `${Math.min(100, (earnings.worked / shiftInfo!.duration) * 100)}%`,
                      },
                    ]}
                  />
                </View>

                <View style={styles.earningsFooter}>
                  <ThemedText style={[styles.earningsFooterText, { color: colors.textSecondary }]}>
                    {earnings.gifted}h guaranteed • {shiftInfo?.duration}h total
                  </ThemedText>
                </View>
              </ModernCard>
            )}
          </>
        ) : (
          <>
            {/* Earnings for INVITED shifts */}
            {totalEarnings && totalEarnings.rate > 0 && (
              <ModernCard style={styles.earningsCard}>
                <View style={styles.cardHeader}>
                  <MaterialCommunityIcons name="currency-usd" size={20} color={colors.emerald?.text || "#10b981"} />
                  <ThemedText type="defaultSemiBold">Potential Earnings</ThemedText>
                </View>

                <View style={styles.earningsGrid}>
                  <View style={styles.earningsStat}>
                    <ThemedText style={[styles.earningsLabel, { color: colors.textSecondary }]}>Rate</ThemedText>
                    <ThemedText style={styles.earningsValue}>${totalEarnings.rate}/hr</ThemedText>
                  </View>

                  <View style={styles.earningsStat}>
                    <ThemedText style={[styles.earningsLabel, { color: colors.textSecondary }]}>Range</ThemedText>
                    <ThemedText style={styles.earningsValue}>
                      ${totalEarnings.minimum.toFixed(0)} - ${totalEarnings.maximum.toFixed(0)}
                    </ThemedText>
                  </View>
                </View>

                <View style={styles.earningsFooter}>
                  <ThemedText style={[styles.earningsFooterText, { color: colors.textSecondary }]}>
                    {totalEarnings.gifted}h guaranteed • {shiftInfo?.duration}h total
                  </ThemedText>
                </View>
              </ModernCard>
            )}

            {/* Accept/Decline Actions for INVITED shifts */}
            {myUI && myUI.status !== "ACCEPTED" && myUI.status !== "DECLINED" && (
              <View style={styles.actionsCard}>
                <ActionButton
                  title={saving === "decline" ? "Declining..." : "Decline"}
                  icon="close"
                  onPress={() => handleAcceptDecline(false)}
                  variant="danger"
                  size="large"
                  loading={saving === "decline"}
                  disabled={saving !== false}
                  fullWidth
                />
                <ActionButton
                  title={saving === "accept" ? "Accepting..." : "Accept"}
                  icon="check"
                  onPress={() => handleAcceptDecline(true)}
                  variant="success"
                  size="large"
                  loading={saving === "accept"}
                  disabled={saving !== false}
                  fullWidth
                />
              </View>
            )}
            {myUI && myUI.status === "DECLINED" && (
              <View style={styles.actionsCard}>
                <ActionButton
                  title={"Declined"}
                  icon="close"
                  variant="danger"
                  onPress={() => null}
                  size="large"
                  loading={saving === "decline"}
                  disabled={true}
                  fullWidth
                />
              </View>
            )}
          </>
        )}

        {/* Expense Action */}
        {myUI?.status === "ACCEPTED" && (
          <ModernCard style={{ marginHorizontal: 16, marginTop: 16 }}>
            <View style={styles.cardHeader}>
              <MaterialCommunityIcons name="receipt" size={20} color={colors.text} />
              <ThemedText type="defaultSemiBold">Expenses</ThemedText>
              {expenses.length > 0 && (
                <View style={[styles.countBadge, { backgroundColor: colors.primary }]}>
                  <ThemedText style={styles.countBadgeText}>{expenses.length}</ThemedText>
                </View>
              )}
            </View>

            {expensesLoading ? (
              <ActivityIndicator size="small" color={colors.primary} />
            ) : expenses.length > 0 ? (
              <View style={styles.expenseList}>
                {expenses.map((expense) => (
                  <View key={expense.id} style={[styles.expenseItem, { backgroundColor: colors.backgroundSecondary }]}>
                    <View style={styles.expenseInfo}>
                      <ThemedText style={styles.expenseName}>
                        {expense.meta_data?.expense_number ? `Expense #${expense.meta_data.expense_number}` : "Expense"}
                      </ThemedText>
                      <ThemedText style={[styles.expenseAmount, { color: colors.textSecondary }]}>
                        ${expense.meta_data?.amount?.toFixed(2) || "0.00"}
                        {expense.meta_data?.category && ` • ${expense.meta_data.category}`}
                      </ThemedText>
                    </View>
                    <StatusBadge
                      variant={getExpenseStatusVariant(expense.status)}
                      text={expense.status || "PENDING"}
                      size="small"
                    />
                  </View>
                ))}
              </View>
            ) : (
              <ThemedText style={{ fontSize: 14, lineHeight: 20, color: colors.textSecondary }}>
                No expenses submitted for this {displayNames?.shifts?.name?.toLowerCase() || "shift"} yet.
              </ThemedText>
            )}

            <Button
              text={expenses.length > 0 ? "Create Another" : "Create Expense"}
              icon="cash-plus"
              iconPosition="left"
              onPress={() => router.navigate(`${pathname}/expense` as any)}
              style={{ marginTop: 12, height: 48 }}
            />
          </ModernCard>
        )}

        {/* Incident Action */}
        {myUI?.status === "ACCEPTED" && (
          <ModernCard style={{ marginHorizontal: 16, marginTop: 16 }}>
            <View style={styles.cardHeader}>
              <MaterialCommunityIcons name="alert-circle-outline" size={20} color={colors.text} />
              <ThemedText type="defaultSemiBold">Incidents</ThemedText>
              {incidents.length > 0 && (
                <View style={[styles.countBadge, { backgroundColor: colors.amber?.border || colors.primary }]}>
                  <ThemedText style={styles.countBadgeText}>{incidents.length}</ThemedText>
                </View>
              )}
            </View>

            {incidentsLoading ? (
              <ActivityIndicator size="small" color={colors.primary} />
            ) : incidents.length > 0 ? (
              <View style={styles.expenseList}>
                {incidents.map((incident) => (
                  <View key={incident.id} style={[styles.expenseItem, { backgroundColor: colors.backgroundSecondary }]}>
                    <View style={styles.expenseInfo}>
                      <ThemedText style={styles.expenseName}>
                        {incident.meta_data?.incident_number || incident.name || "Incident"}
                      </ThemedText>
                      <ThemedText style={[styles.expenseAmount, { color: colors.textSecondary }]}>
                        {incident.name}
                      </ThemedText>
                    </View>
                    <StatusBadge
                      variant={getIncidentStatusVariant(incident.status)}
                      text={incident.status || "PENDING REVIEW"}
                      size="small"
                    />
                  </View>
                ))}
              </View>
            ) : (
              <ThemedText style={{ fontSize: 14, lineHeight: 20, color: colors.textSecondary }}>
                No incidents reported for this {displayNames?.shifts?.name?.toLowerCase() || "shift"} yet.
              </ThemedText>
            )}

            <Button
              text={incidents.length > 0 ? "Report Another" : "Report Incident"}
              icon="alert-plus"
              iconPosition="left"
              onPress={() => router.navigate(`${pathname}/incident` as any)}
              style={{ marginTop: 12, height: 48 }}
            />
          </ModernCard>
        )}

        {/* Comments Section */}
        {shift?.id && false && (
          <ModernCard>
            <Comments entityType="shifts" entityId={shift?.id ?? ""} onCommentsUpdate={handleCommentsUpdate} />
          </ModernCard>
        )}

        {/* Project Details */}
        {shift.project?.description && (
          <ModernCard>
            <View style={styles.cardHeader}>
              <MaterialCommunityIcons name="information-outline" size={20} color={colors.text} />
              <ThemedText type="defaultSemiBold">{displayNames?.projects?.name || "Project"} Details</ThemedText>
            </View>
            <ThemedText style={{ color: colors.textSecondary }}>{shift.project.description}</ThemedText>
          </ModernCard>
        )}
      </ScrollView>

      {/* QR Code Modal - Supervisor Only */}
      {qrModalVisible && shiftId && (
        <Modal onUnmount={() => setQrModalVisible(false)}>
          <QRCodeModal shiftId={shiftId} onClose={() => setQrModalVisible(false)} onOpenKiosk={handleOpenKiosk} />
        </Modal>
      )}

      {/* Clock Out Survey Modal - Crew Only */}
      {showClockOutSurvey && (
        <Modal onUnmount={() => setShowClockOutSurvey(false)}>
          <ClockOutSurvey
            startTime={timeCard.clockedIn}
            endTime={new Date()}
            onComplete={handleSurveyComplete}
            onCancel={() => setShowClockOutSurvey(false)}
          />
        </Modal>
      )}

      {/* Supervisor Contact Modal - When break issues detected */}
      {showSupervisorContact && (
        <Modal onUnmount={() => setShowSupervisorContact(false)}>
          <SupervisorContactModal
            supervisors={shift?.shift_supervisors}
            shiftId={shiftId}
            onRetry={() => {
              setShowSupervisorContact(false);
              setShowClockOutSurvey(true);
            }}
            onCancel={() => setShowSupervisorContact(false)}
          />
        </Modal>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  cardHeader: {
    gap: 8,
    flexDirection: "row",
  },

  // Hero
  hero: {
    paddingTop: 60,
    paddingBottom: 32,
  },
  heroContent: {
    gap: 16,
  },
  heroProject: {
    fontSize: 28,
    fontWeight: "800",
    color: "white",
  },
  heroProjectSubtitle: {
    fontSize: 16,
    fontWeight: "500",
    color: "white",
    opacity: 0.85,
    marginTop: -8,
  },
  heroDetailsRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
  },
  heroTimeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  heroTime: {
    fontSize: 15,
    color: "white",
    opacity: 0.9,
    fontWeight: "500",
  },
  heroStatus: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },

  // Cards
  positionsCard: {
    marginHorizontal: 16,
    marginTop: 16,
  },
  timeCard: {
    marginHorizontal: 16,
    marginTop: 16,
  },
  earningsCard: {
    marginHorizontal: 16,
    marginTop: 16,
  },
  attachmentsCard: {
    marginHorizontal: 16,
    marginTop: 16,
  },
  // Positions
  positionSeparator: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "rgba(0,0,0,0.1)",
  },

  // Notes
  notesSection: {
    gap: 8,
    marginTop: 12,
    marginBottom: 4,
  },
  noteItem: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
  },
  noteMessage: {
    fontSize: 12,
    lineHeight: 20,
  },

  // Time Tracking
  timeStats: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "rgba(0,0,0,0.1)",
  },
  timeStat: {
    alignItems: "center",
  },
  timeStatLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  timeStatValue: {
    fontSize: 20,
    fontWeight: "600",
  },
  actionGrid: {
    gap: 12,
  },
  secondaryActions: {
    flexDirection: "row",
    gap: 12,
    marginTop: 8,
  },

  // Earnings
  earningsGrid: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginBottom: 16,
  },
  earningsStat: {
    alignItems: "center",
  },
  earningsLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  earningsValue: {
    fontSize: 24,
    fontWeight: "700",
  },
  earningsBar: {
    height: 8,
    borderRadius: 4,
    overflow: "hidden",
    marginBottom: 12,
  },
  earningsProgress: {
    height: "100%",
    borderRadius: 4,
  },
  earningsFooter: {
    alignItems: "center",
  },
  earningsFooterText: {
    fontSize: 13,
  },

  // Actions
  actionsCard: {
    flexDirection: "row",
    gap: 12,
    margin: 16,
  },

  // Clock-in instructions
  clockInInstructions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  clockInInstructionsText: {
    fontSize: 14,
    fontWeight: "500",
    flex: 1,
  },

  // Expenses
  expenseList: {
    gap: 8,
    marginTop: 12,
  },
  expenseItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    borderRadius: 8,
  },
  expenseInfo: {
    flex: 1,
    marginRight: 12,
  },
  expenseName: {
    fontSize: 14,
    fontWeight: "600",
  },
  expenseAmount: {
    fontSize: 12,
    marginTop: 2,
  },
  countBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    marginLeft: "auto",
  },
  countBadgeText: {
    fontSize: 12,
    fontWeight: "700",
    color: "white",
  },
});
